import { useCallback, useEffect, useState } from 'preact/compat';

interface FormElements {
  urlInputElement: HTMLInputElement | null;
  submitButtonElement: HTMLAnchorElement | null;
  submitButtonMobileElement: HTMLInputElement | null;
}

interface UseFormElementsReturn extends FormElements {
  url: string;
  setSubmitButtonsEnabled: (enabled: boolean) => void;
  handleFormSubmit: (event: Event) => Promise<void>;
}

export function useFormElements(
  onSubmitUrl: (url: string) => Promise<void>,
  isLoading: boolean,
): UseFormElementsReturn {
  const [url, setUrl] = useState('');

  const urlInputElement = document.getElementById(
    'url-input',
  ) as HTMLInputElement | null;
  const submitButtonElement = document.getElementById(
    'submit-button',
  ) as HTMLAnchorElement | null;
  const submitButtonMobileElement = document.getElementById(
    'submit-button-mobile',
  ) as HTMLInputElement | null;

  const onInput = useCallback((event: Event) => {
    setUrl((event.target as HTMLInputElement).value);
  }, []);

  const setSubmitButtonsEnabled = useCallback(
    (enabled: boolean) => {
      if (!submitButtonElement || !submitButtonMobileElement) {
        return;
      }

      submitButtonElement.style.pointerEvents = enabled ? 'auto' : 'none';
      submitButtonElement.style.opacity = enabled ? '1' : '0.5';
      submitButtonMobileElement.disabled = !enabled;
      submitButtonMobileElement.style.opacity = enabled ? '1' : '0.5';
    },
    [submitButtonElement, submitButtonMobileElement],
  );

  const handleFormSubmit = useCallback(
    async (event: Event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (isLoading || !url) {
        urlInputElement?.form?.reportValidity();

        return;
      }

      setSubmitButtonsEnabled(false);
      await onSubmitUrl(url);
      setSubmitButtonsEnabled(true);
    },
    [isLoading, url, urlInputElement, onSubmitUrl, setSubmitButtonsEnabled],
  );

  useEffect(() => {
    if (
      !urlInputElement ||
      !submitButtonElement ||
      !submitButtonMobileElement
    ) {
      return;
    }

    urlInputElement.addEventListener('input', onInput);
    submitButtonElement.addEventListener('click', handleFormSubmit);
    submitButtonMobileElement.addEventListener('click', handleFormSubmit);

    return () => {
      urlInputElement.removeEventListener('input', onInput);
      submitButtonElement.removeEventListener('click', handleFormSubmit);
      submitButtonMobileElement.removeEventListener('click', handleFormSubmit);
    };
  }, [
    onInput,
    handleFormSubmit,
    urlInputElement,
    submitButtonElement,
    submitButtonMobileElement,
  ]);

  return {
    url,
    urlInputElement,
    submitButtonElement,
    submitButtonMobileElement,
    setSubmitButtonsEnabled,
    handleFormSubmit,
  };
}
